// src/features/records/components/MapView/utils/geohash.js
import { geohashForLocation, geohashQueryBounds } from 'geofire-common';

/**
 * Generate a geohash for a location
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} precision - Geohash precision (default 9)
 * @returns {string} Geohash string
 */
export const generateGeohash = (lat, lng, precision = 9) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    throw new Error('Invalid coordinates');
  }
  
  if (lat < -90 || lat > 90) {
    throw new Error('Latitude must be between -90 and 90');
  }
  
  if (lng < -180 || lng > 180) {
    throw new Error('Longitude must be between -180 and 180');
  }
  
  return geohashForLocation([lat, lng], precision);
};

/**
 * Get geohash query bounds for a geographic area
 * @param {Array} center - [lat, lng] center point
 * @param {number} radiusInM - Radius in meters
 * @returns {Array} Array of [startHash, endHash] pairs
 */
export const getGeohashBounds = (center, radiusInM) => {
  return geohashQueryBounds(center, radiusInM);
};

/**
 * Calculate distance between two points (Haversine formula)
 * @param {number} lat1 
 * @param {number} lng1 
 * @param {number} lat2 
 * @param {number} lng2 
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);